import { useState } from "react";
import { Bookmark, Heart, MessageCircle, MoreHorizontal, Send } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import PosterArtwork from "../../entities/post/ui/PosterArtwork";

const numberFormat = new Intl.NumberFormat("zh-CN");

export function Story({ story }) {
  return (
    <button className="story">
      <span className={`story-ring${story.mine ? " mine" : ""}`}>
        <img src={story.avatar} alt="" />
        {story.mine && <span className="story-add">+</span>}
      </span>
      <span>{story.mine ? "你的快拍" : story.name}</span>
    </button>
  );
}

export default function PostCard({ post, onToggleLike, onToggleSave }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [shareNotice, setShareNotice] = useState("");
  const [captionExpanded, setCaptionExpanded] = useState(false);

  async function sharePost() {
    const shareUrl = `${window.location.origin}/posts/${post.id}`;
    try {
      if (navigator.share) {
        await navigator.share({ title: post.caption || post.author, url: shareUrl });
      } else {
        await navigator.clipboard.writeText(shareUrl);
      }
      setShareNotice("链接已复制");
    } catch (error) {
      if (error?.name !== "AbortError") setShareNotice("分享暂不可用");
    } finally {
      window.setTimeout(() => setShareNotice(""), 2200);
    }
  }

  return (
    <article className="post-card">
      <header className="post-header">
        <NavLink to={post.authorId ? `/users/${post.authorId}` : "/profile"} className="post-author">
          <img className="avatar avatar-md" src={post.avatar} alt="" />
          <span>
            <strong>{post.author}</strong>
            {post.location ? <small>{post.location}</small> : null}
          </span>
        </NavLink>
        <button className="icon-button" aria-label="更多操作">
          <MoreHorizontal size={22} />
        </button>
      </header>
      <NavLink className="post-media" to={`/posts/${post.id}`} state={{ backgroundLocation: location }} aria-label={`查看${post.author}的作品详情`}>
        {post.image ? (
          <img src={post.image} alt={`${post.author}发布的平面设计作品`} />
        ) : (
          <PosterArtwork artwork={post.artwork} />
        )}
      </NavLink>
      <div className="post-actions">
        <div>
          <span className="action-with-count">
            <button
            className={`icon-button action-button${post.liked ? " liked" : ""}`}
            onClick={() => onToggleLike(post.id)}
            aria-label={post.liked ? "取消点赞" : "点赞"}
            >
              <Heart size={27} fill={post.liked ? "currentColor" : "none"} />
            </button>
            <span>{numberFormat.format(post.likes)}</span>
          </span>
          <span className="action-with-count">
          <button className="icon-button action-button" aria-label="评论" onClick={() => navigate(`/posts/${post.id}`, { state: { backgroundLocation: location, focusComments: true } })}>
              <MessageCircle size={26} />
            </button>
            <span>{numberFormat.format(post.comments)}</span>
          </span>
          <button className="icon-button action-button" aria-label="分享" onClick={sharePost}>
            <Send size={25} />
          </button>
        </div>
        <button className="icon-button action-button" onClick={() => onToggleSave(post.id)} aria-label={post.saved ? "取消收藏" : "收藏"}>
          <Bookmark size={26} fill={post.saved ? "currentColor" : "none"} />
        </button>
      </div>
      {shareNotice ? <p className="share-notice" role="status">{shareNotice}</p> : null}
      <div className="post-copy">
        <p className={`post-caption${captionExpanded ? " expanded" : ""}`}>
          <b>{post.username}</b> {post.caption}
        </p>
        {post.caption?.length > 48 ? (
          <button className="muted-link caption-more" onClick={() => setCaptionExpanded((expanded) => !expanded)}>
            {captionExpanded ? "收起" : "查看"}
          </button>
        ) : null}
      </div>
    </article>
  );
}
